import { PureComponent, useContext } from "react";
import { connect } from "react-redux";
import {
  Button,
  useAccordionButton,
  Modal,
  Form,
  Alert,
  Accordion,
  AccordionContext,
  Row,
  Col,
} from "react-bootstrap";

import { validateEmail } from "../../../utils/regex";
import * as userCompaniesActions from "../../../state/user-companies";
import * as toastActions from "../../../state/toasts";
import * as date from "../../../utils/date";
import Loader from "../../../common/Loader";
import { ToastType } from "../../../common/Toasts";
import {
  getRegistryCompanies,
  createUserCompany,
  editUserCompany,
} from "../../../services/turgoil-api";
import classNames from "classnames";

// function CustomAccordionToggle({ children }) {
//   const decoratedOnClick = useAccordionButton("");

//   return (
//     <Button type="button" variant="link" onClick={decoratedOnClick}>
//       {children}
//     </Button>
//   );
// }

type Props = {
  getUserCompanies: typeof userCompaniesActions.getUserCompanies;
  showToast: typeof toastActions.showToast;
  companies: any[];
  user: any;
};

type C = {
  registryCode: string;
  contactPhone: string;
  contactEmail: string;
};

type State = {
  openCreateModal: boolean;
  openEditModal: boolean;
  fetchingCompany: boolean;
  fetchedCompanyInfo: any;
  loading: boolean;
  errors: Record<string, string>;
  company: C;
  successMessage?: string;
};

function CustomAccordionToggle({
  children,
  eventKey,
  callback,
}: {
  children: React.ReactNode;
  eventKey: string;
  callback?: (e: string) => void;
}) {
  const currentEventKey = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey),
  );

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <button
      type="button"
      className={classNames("collapse-button", { active: isCurrentEventKey })}
      onClick={decoratedOnClick}
    >
      {children}
    </button>
  );
}

function getCompanyStatusDisplayName(companyStatus: string): string {
  switch (companyStatus) {
    case "R":
      return "Registrisse kantud";
    case "L":
      return "Likvideerimisel";
    case "N":
      return "Pankrotis";
    case "K":
      return "Kustutatud";
    default:
      return "";
  }
}

class Company extends PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      openCreateModal: false,
      fetchingCompany: false,
      openEditModal: false,
      fetchedCompanyInfo: undefined,
      loading: false,
      errors: {},
      company: {
        registryCode: "",
        contactPhone: "",
        contactEmail: "",
      },
    };
  }

  componentDidMount() {
    const { user, getUserCompanies } = this.props;
    getUserCompanies(user.id);
  }

  openCreateModal = () => {
    this.setState({
      fetchedCompanyInfo: undefined,
      openCreateModal: true,
      company: {
        registryCode: "",
        contactPhone: "",
        contactEmail: "",
      },
    });
  };

  validate = () => {
    const errors: Record<string, string> = {};
    const { company } = this.state;

    if (company.registryCode && company.registryCode.length !== 8) {
      errors.registryCodeError = "Registrikoodi pikkus peab olema 8 tähemärki.";
    }
    if (company.contactEmail && !validateEmail(company.contactEmail)) {
      errors.contactEmailError =
        "See ei ole korrektne e-posti aadress. Korrektne on näiteks info@turgoil.com";
    }
    if (company.contactPhone && company.contactPhone.length > 20) {
      errors.contactPhoneError =
        "Telefoni numbri maksimaalne pikkus on 20 tähemärki.";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleCreate = async (e) => {
    e.preventDefault();

    if (!this.validate()) {
      return;
    }

    const { company } = this.state;
    const { user, getUserCompanies, showToast } = this.props;

    this.setState({ loading: true, successMessage: undefined });
    try {
      const res = await createUserCompany(user.id, company);
      const emailMessage = res.email ? `Teie taotlus juriidilise isiku lisamise kohta platvormile on edastatud äriregistris olevale e-posti aadressile ${res.email} ja ootab kinnitamist. ` : "Teie taotlus juriidilise isiku lisamise kohta platvormile on edastatud platvormi haldajale. ";
      this.setState({
        successMessage: `Ettevõte edukalt lisatud. ${emailMessage}Peale kinnitamist on Teil võimalik korraldada vedelkütuste hankeid lisatud ettevõtte nimel. Vajadusel pöörduge platvormi haldaja poole info@turgoil.com.`,
      })
    } catch (err) {
      this.setState({ loading: false });
      showToast({
        title: "Päring ebaõnnestus.",
        text: "Selline ettevõtte on juba registreeritud. Kui tegemist on Teie ettevõttega, siis võtke ühendust klienditoega info@turgoil.com.",
        type: ToastType.Error,
      });

      return;
    }

    try {
      // Silent success
      await getUserCompanies(user.id);
    } catch (err) {
      showToast({
        title: "Päring ebaõnnestus.",
        text: err.message,
        type: ToastType.Error,
      });
    }

    this.setState({
      openCreateModal: false,
      loading: false,
      company: {} as C,
    });
  };

  handleEdit = async (e) => {
    e.preventDefault();

    if (!this.validate()) {
      return;
    }

    const { company } = this.state;
    const { companies, getUserCompanies, showToast, user } = this.props;

    const foundCompany = companies.find(
      (c) => c.registryCode === company.registryCode,
    );
    if (!foundCompany) {
      return;
    }

    this.setState({ loading: true });

    try {
      await editUserCompany(user.id, foundCompany.id, {
        contactPhone: company.contactPhone,
        contactEmail: company.contactEmail,
      });
      showToast({
        title: "Päring õnnestus.",
        text: "Ettevõte edukalt muudetud.",
        type: ToastType.Success,
      });
    } catch (err) {
      this.setState({ loading: false });
      showToast({
        title: "Päring ebaõnnestus.",
        text: err.message,
        type: ToastType.Error,
      });

      return;
    }

    try {
      // Silent success
      await getUserCompanies(user.id);
    } catch (err) {
      showToast({
        title: "Päring ebaõnnestus.",
        text: err.message,
        type: ToastType.Error,
      });
    }

    this.setState({
      openEditModal: false,
      company: {
        registryCode: "",
        contactPhone: "",
        contactEmail: "",
      },
      loading: false,
    });
  };

  handleCompanyChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      company: {
        ...prevState.company,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [`${name}Error`]: "",
      },
    }));
  };

  handleRegistryCodeChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      company: {
        ...prevState.company,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [`${name}Error`]: "",
      },
    }));

    if (e.target.value.length === 8) {
      this.fetchCompanyInformation(e.target.value);
    }
  };

  fetchCompanyInformation = (registryCode) => {
    const { showToast } = this.props;

    this.setState({
      fetchingCompany: true,
      fetchedCompanyInfo: undefined,
    });

    getRegistryCompanies({ registryCode })
      .then((companiesRes) => {
        if (!companiesRes.length) {
          this.setState({ fetchingCompany: false });
          showToast({
            title: "Päring ebaõnnestus.",
            text: "Kahjuks sellist ettevõtet ei leitud. Kui olete veendunud, et sellise registrikoodiga ettevõtte on olemas, siis võtke klienditoega ühendust.",
            type: ToastType.Error,
          });

          return;
        }

        this.setState({
          fetchedCompanyInfo: companiesRes[0],
          fetchingCompany: false,
        });
      })
      .catch(() => {
        this.setState({
          fetchingCompany: false,
        });
      });
  };

  closeModal = () => {
    this.setState({
      openCreateModal: false,
      openEditModal: false,
      errors: {},
      company: {} as C,
    });
  };

  openEditModal = (companyId) => {
    const { companies } = this.props;
    const company = companies.find((c) => c.id === companyId);

    this.setState({
      openEditModal: true,
      errors: {},
      company: {
        registryCode: company.registryCode,
        contactPhone: company.contactPhone,
        contactEmail: company.contactEmail,
      },
    });
  };

  render() {
    const { companies, user } = this.props;
    const {
      openCreateModal,
      openEditModal,
      loading,
      company,
      errors,
      fetchedCompanyInfo,
      fetchingCompany,
      successMessage,
    } = this.state;

    return (
      <>
        <div className="settings__content-title">
          Ettevõte
          <Button type="button" className="ms-auto" variant="outline-primary" onClick={this.openCreateModal}>
            + Lisa ettevõte
          </Button>
        </div>

        {successMessage && <Alert variant="warning">{successMessage}</Alert>}
        {loading && <Loader />}
        {!loading && (
          <>
            <Accordion>
              {companies.map((c) => (
                <Accordion.Item key={c.registryCode} eventKey={c.registryCode}>
                  <Accordion.Header>
                    {c.companyName}
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="row">
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="companyName">Ettevõtte nimi</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="companyName"
                            className="form-control"
                            disabled
                            plaintext
                            value={c.companyName}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="registryCode">Registrikood</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="registryCode"
                            className="form-control"
                            disabled
                            plaintext
                            value={c.registryCode}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="contactPhone">Telefoni number</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="contactPhone"
                            className="form-control"
                            disabled
                            plaintext
                            value={c.contactPhone}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="contactEmail">E-posti aadress</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="contactEmail"
                            className="form-control"
                            disabled
                            plaintext
                            value={c.contactEmail}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="regDateTime">Registreerimise kuupäev</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="regDateTime"
                            className="form-control"
                            disabled
                            plaintext
                            value={date.getDateTime(c.regDatetime)}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="status">Staatus</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="status"
                            className="form-control"
                            disabled
                            plaintext
                            value={getCompanyStatusDisplayName(c.companyStatus)}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group className="mb-2" as={Row}>
                        <Form.Label column sm={4} htmlFor="status">Kinnitatud</Form.Label>
                        <Col sm={8}>
                          <Form.Control
                            name="confirmed"
                            className="form-control"
                            disabled
                            plaintext
                            value={c.confirmed ? "Jah" : "Ei"}
                          />
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="d-flex">
                      {c.userId === user.id && (
                        <Button className="ms-auto" onClick={() => this.openEditModal(c.id)}>
                          Muuda
                        </Button>
                      )}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
            {!companies.length && (
              <div>Te ei ole lisanud ühtegi ettevõtet.</div>
            )}
          </>
        )}

        <Modal
          show={openCreateModal || openEditModal}
          animation={false}
          onHide={this.closeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            {openEditModal ? "Ettevõtte muutmine" : "Ettevõtte lisamine"}
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-xs-12 col-sm-6">
                {fetchingCompany && <Loader />}
                <Form
                  onSubmit={
                    openCreateModal ? this.handleCreate : this.handleEdit
                  }
                >
                  {!fetchingCompany && fetchedCompanyInfo && (
                    <Form.Group className="mb-2">
                      <Form.Label>Ettevõtte nimi</Form.Label>
                      <Form.Control
                        readOnly
                        plaintext
                        value={fetchedCompanyInfo.companyName}
                      />
                    </Form.Group>
                  )}
                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="registryCode">Registrikood</Form.Label>
                    <Form.Control
                      id="registryCode"
                      name="registryCode"
                      value={company.registryCode}
                      readOnly={openEditModal}
                      plaintext={openEditModal}
                      onChange={this.handleRegistryCodeChange}
                      maxLength={8}
                      isInvalid={Boolean(errors.registryCodeError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.registryCodeError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="contactEmail">
                      Kontakt e-posti aadress
                    </Form.Label>
                    <Form.Control
                      id="contactEmail"
                      name="contactEmail"
                      value={company.contactEmail}
                      onChange={this.handleCompanyChange}
                      isInvalid={Boolean(errors.contactEmailError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.contactEmailError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="contactPhone">
                      Kontakt telefon
                    </Form.Label>
                    <Form.Control
                      id="contactPhone"
                      name="contactPhone"
                      value={company.contactPhone}
                      onChange={this.handleCompanyChange}
                      isInvalid={Boolean(errors.contactPhoneError)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.contactPhoneError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Form>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button type="button" variant="link" onClick={this.closeModal}>
              Tühista
            </Button>
            <Button
              style={{ marginLeft: "auto" }}
              type="button"
              onClick={
                openCreateModal ? this.handleCreate : this.handleEdit
              }
            >
              Salvesta
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    companies: state.userCompaniesReducer.companies,
    user: state.userReducer.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getUserCompanies: (userId) =>
      dispatch(userCompaniesActions.getUserCompanies(userId)),
    showToast: (toast) => dispatch(toastActions.showToast(toast)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Company);
